import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Activity, Clock, History, AlertTriangle, Bell } from 'lucide-react';
import Layout from '../components/Layout';
import PollingStatusMonitor from '../components/PollingStatusMonitor';
import StockHealthMonitor from '../components/StockHealthMonitor';
import IssuesBar from '../components/IssuesBar';
import SignalHistoryPanel from '../components/SignalHistoryPanel';
import NotificationHistory from '../components/NotificationHistory';
import { apiService } from '../services/api';

const TAB_SLUGS = {
  'today-status': 'today',
  'historical-health': 'historical',
  'signal-history': 'signals',
  'notifications': 'notifications',
};
const SLUG_FROM_ID = Object.fromEntries(Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]));

// Historical Health sections, in render order — the issues bar aggregates
// verdicts from every one of them before it may claim green.
const HEALTH_MARKETS = ['india', 'us', 'asia_pacific', 'europe', 'crypto', 'precious_metals'];
const HEALTH_MARKET_NAMES = { india: 'India', us: 'US', asia_pacific: 'Asia-Pacific', europe: 'Europe', crypto: 'Crypto', precious_metals: 'Precious Metals' };

const Dashboard = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // One global timeline window for ALL Historical Health sections.
  const [healthPeriod, setHealthPeriod] = useState(90);
  // Issues bar over the Historical Health sections: each section reports its
  // red facts up here as it loads (null = still checking, never a false green).
  const [healthIssues, setHealthIssues] = useState({});
  const [healthFlash, setHealthFlash] = useState(null);
  const flashTimer = useRef(null);
  const reportIssues = useCallback((market, issues) => {
    setHealthIssues(prev => ({ ...prev, [market]: issues }));
  }, []);
  const jumpToHealth = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHealthFlash(id);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setHealthFlash(null), 2600);
  }, []);

  const activeTab = (tab && TAB_SLUGS[tab]) || 'today';

  const setActiveTab = (id) => {
    navigate(`/dashboard/${SLUG_FROM_ID[id]}`, { replace: true });
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  // Redirect bare /dashboard to /dashboard/today-status
  useEffect(() => {
    if (!tab) navigate('/dashboard/today-status', { replace: true });
  }, [tab, navigate]);

  const tabs = [
    {
      id: 'today',
      label: "Today's Health",
      icon: Clock,
      description: 'Live freshness, close & model checks',
    },
    {
      id: 'historical',
      label: 'Historical Health',
      icon: History,
      description: 'Last 7–90 days data quality',
    },
    {
      id: 'signals',
      label: 'Signal History',
      icon: AlertTriangle,
      description: 'Audit trail & flip detection',
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Was every alert & plan delivered?',
    },
  ];

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-400 mb-2">Error loading dashboard</p>
            <p className="text-dark-400 text-sm mb-4">{error}</p>
            <button
              onClick={() => setError(null)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <Activity className="w-8 h-8 text-primary-400" />
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          </div>
          <p className="text-dark-300">Monitor data polling and quality across all markets</p>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-card">
          <div className="flex space-x-2 border-b border-dark-700">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all relative ${
                    activeTab === tab.id
                      ? 'text-primary-400'
                      : 'text-dark-400 hover:text-dark-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">{tab.label}</div>
                    <div className="text-xs opacity-75">{tab.description}</div>
                  </div>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-400"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'today' && <PollingStatusMonitor />}

        {activeTab === 'historical' && (
          <div className="space-y-6">
            {/* Issues bar — every red fact across all markets, click to jump */}
            {(() => {
              const reported = HEALTH_MARKETS.filter(m => Array.isArray(healthIssues[m]));
              const issues = reported.flatMap(m => healthIssues[m]);
              const coming = HEALTH_MARKETS.filter(m => !Array.isArray(healthIssues[m])).map(m => HEALTH_MARKET_NAMES[m]);
              return <IssuesBar issues={issues} onJump={jumpToHealth}
                scanning={coming.length > 0} scanningWhat={`still coming: ${coming.join(' · ')}`} />;
            })()}
            {/* Global timeline selector — controls every section below at once */}
            <div className="dashboard-card flex items-center justify-between py-3">
              <span className="text-sm font-medium text-dark-200">Timeline window
                <span className="ml-2 text-xs text-dark-400">applies to all sections</span>
              </span>
              <div className="flex space-x-1">
                {[7, 14, 30, 60, 90].map((d) => (
                  <button key={d} onClick={() => setHealthPeriod(d)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${healthPeriod === d ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-300 hover:bg-dark-600'}`}>
                    {d}D
                  </button>
                ))}
              </div>
            </div>
            {HEALTH_MARKETS.map(m => (
              <StockHealthMonitor key={m} market={m} period={healthPeriod}
                onIssues={reportIssues} flashKey={healthFlash} />
            ))}
          </div>
        )}

        {activeTab === 'signals' && <SignalHistoryPanel />}
        {activeTab === 'notifications' && <NotificationHistory />}
      </div>
    </Layout>
  );
};

export default Dashboard;
